import { useState, useEffect, useRef } from 'react';
import { useExpenseByIdQuery } from '../../../../__generated__/graphql';
import { findTravelExpenseItem, POLLING_INTERVAL } from '../carMileageHelpers';

type UseCarMileageDistanceProps = {
  expenseId: string | undefined;
  travelExpenseItemId: string | null;
  hasLoadedInitialData: boolean;
  initialKmDistance?: number | null | undefined;
  initialDistanceSyncInProgress?: boolean;
};

export const useCarMileageDistance = ({
  expenseId,
  travelExpenseItemId,
  hasLoadedInitialData,
  initialKmDistance = null,
  initialDistanceSyncInProgress = false,
}: UseCarMileageDistanceProps) => {
  const [kmDistance, setKmDistance] = useState<number | null>(
    initialKmDistance,
  );
  const [distanceSyncInProgress, setDistanceSyncInProgress] = useState(
    initialDistanceSyncInProgress,
  );

  const { refetch: refetchExpense } = useExpenseByIdQuery({
    variables: { id: expenseId || '' },
    skip: !expenseId,
  });

  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!hasLoadedInitialData || !expenseId || !travelExpenseItemId) {
      return;
    }

    const pollData = async () => {
      try {
        const result = await refetchExpense();
        if (result.data?.expense) {
          const travelItem = findTravelExpenseItem(
            result.data.expense.expenseItems,
          );

          if (travelItem) {
            if (travelItem.flair__Km_Distance__c !== kmDistance) {
              setKmDistance(travelItem.flair__Km_Distance__c ?? null);
            }

            const syncInProgress =
              travelItem.flair__Distance_Sync_In_Progress__c || false;
            if (syncInProgress !== distanceSyncInProgress) {
              setDistanceSyncInProgress(syncInProgress);
            }
          }
        }
      } catch (err) {
        console.error('Error polling expense data:', err);
      }
    };

    pollingTimerRef.current = setInterval(pollData, POLLING_INTERVAL);

    return () => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
      }
    };
  }, [
    hasLoadedInitialData,
    expenseId,
    travelExpenseItemId,
    kmDistance,
    distanceSyncInProgress,
    refetchExpense,
  ]);

  return {
    kmDistance,
    distanceSyncInProgress,
  };
};
